import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  RefreshControl,
  Alert,
  TouchableOpacity,
  Dimensions,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { LineChart, BarChart, PieChart } from 'react-native-chart-kit';

import { useSocialLearningAnalytics } from '../hooks/useSocialLearningAnalytics';
import { useAppSelector } from '../store';
import { selectUserProfile } from '../store/slices/userSlice';
import { PeerBenchmarkCard } from '../components/ai/PeerBenchmarkCard';
import { EffectivenessInsightCard } from '../components/ai/EffectivenessInsightCard';
import { CollaborativeAchievementCard } from '../components/ai/CollaborativeAchievementCard';
import { EngagementOptimizationCard } from '../components/ai/EngagementOptimizationCard';
import { ABTestParticipationCard } from '../components/ai/ABTestParticipationCard';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { ErrorMessage } from '../components/ui/ErrorMessage';

const { width: screenWidth } = Dimensions.get('window');
import { theme } from '../theme';
import { logger } from '../utils/logger';

export const SocialLearningAnalyticsScreen: React.FC = () => {
  const navigation = useNavigation();
  const user = useAppSelector(selectUserProfile);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTab, setSelectedTab] = useState<
    'overview' | 'benchmarks' | 'achievements' | 'optimization' | 'tests'
  >('overview');

  const {
    peerComparisons,
    learningEffectiveness,
    collaborativeAchievements,
    engagementOptimization,
    activeABTests,
    loading,
    error,
    topPerformingPeers,
    activeAchievements,
    completedAchievements,
    highImpactOptimizations,
    userPerformanceInsights,
    refreshAllData,
    generateOptimization,
  } = useSocialLearningAnalytics();

  useEffect(() => {
    if (user?.user_id) {
      refreshAllData(user.user_id);
    }
  }, [user?.user_id, refreshAllData]);

  const handleRefresh = async () => {
    if (!user?.user_id) return;

    setRefreshing(true);
    try {
      await refreshAllData(user.user_id);
    } catch (error) {
      logger.error('Failed to refresh data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleGenerateOptimization = async () => {
    if (!user?.user_id) return;

    try {
      await generateOptimization(user.user_id);
    } catch (error) {
      logger.error('Failed to generate optimization:', error);
    }
  };

  const renderOverviewTab = () => (
    <View style={styles.tabContent}>
      {/* Performance Summary */}
      <View style={styles.summaryCard}>
        <Text style={styles.cardTitle}>Your Social Learning Performance</Text>
        <View style={styles.summaryGrid}>
          <View style={styles.summaryItem}>
            <Text style={styles.summaryValue}>
              {userPerformanceInsights.overallRanking}%
            </Text>
            <Text style={styles.summaryLabel}>Overall Ranking</Text>
          </View>
          <View style={styles.summaryItem}>
            <Text style={styles.summaryValue}>
              {learningEffectiveness?.effectivenessRatio.toFixed(1) || 'N/A'}x
            </Text>
            <Text style={styles.summaryLabel}>Social Boost</Text>
          </View>
          <View style={styles.summaryItem}>
            <Text style={styles.summaryValue}>
              {completedAchievements.length}
            </Text>
            <Text style={styles.summaryLabel}>Achievements</Text>
          </View>
          <View style={styles.summaryItem}>
            <Text style={styles.summaryValue}>{activeAchievements.length}</Text>
            <Text style={styles.summaryLabel}>In Progress</Text>
          </View>
        </View>
      </View>

      {/* Quick Insights */}
      <View style={styles.insightsCard}>
        <Text style={styles.cardTitle}>Key Insights</Text>
        <View style={styles.insightsList}>
          {userPerformanceInsights.strongAreas.length > 0 && (
            <View style={styles.insightItem}>
              <Ionicons name='trending-up' size={20} color='#4CAF50' />
              <Text style={styles.insightText}>
                Strong in: {userPerformanceInsights.strongAreas.join(', ')}
              </Text>
            </View>
          )}
          {userPerformanceInsights.improvementAreas.length > 0 && (
            <View style={styles.insightItem}>
              <Ionicons name='bulb-outline' size={20} color='#FF9800' />
              <Text style={styles.insightText}>
                Growth opportunities:{' '}
                {userPerformanceInsights.improvementAreas.join(', ')}
              </Text>
            </View>
          )}
          {learningEffectiveness &&
            learningEffectiveness.effectivenessRatio > 1.2 && (
              <View style={styles.insightItem}>
                <Ionicons name='people' size={20} color='#2196F3' />
                <Text style={styles.insightText}>
                  You learn{' '}
                  {Math.round(
                    (learningEffectiveness.effectivenessRatio - 1) * 100
                  )}
                  % faster with peers!
                </Text>
              </View>
            )}
        </View>
      </View>

      {/* Recent Achievements */}
      {completedAchievements.slice(0, 3).map(achievement => (
        <CollaborativeAchievementCard
          key={achievement.id}
          achievement={achievement}
          onCelebrate={() => {}}
          onShare={() => {}}
        />
      ))}

      {/* Optimization Suggestions */}
      {highImpactOptimizations.slice(0, 2).map((opportunity, index) => (
        <EngagementOptimizationCard
          key={index}
          opportunity={opportunity}
          onImplement={() => {}}
        />
      ))}
    </View>
  );

  const renderBenchmarksTab = () => (
    <View style={styles.tabContent}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Peer Benchmarking</Text>
        <Text style={styles.sectionSubtitle}>
          Compare your progress with anonymized peer data
        </Text>
      </View>

      {peerComparisons.map((benchmark, index) => (
        <PeerBenchmarkCard
          key={index}
          benchmark={benchmark}
          onViewDetails={() => {}}
        />
      ))}

      {topPerformingPeers.length > 0 && (
        <View style={styles.topPeersCard}>
          <Text style={styles.cardTitle}>Top Performing Peers</Text>
          <Text style={styles.cardSubtitle}>
            Learn from these high-achieving community members
          </Text>
          {topPerformingPeers.slice(0, 5).map((peer, index) => (
            <View key={peer.anonymizedId} style={styles.peerItem}>
              <View style={styles.peerRank}>
                <Text style={styles.rankText}>#{index + 1}</Text>
              </View>
              <View style={styles.peerInfo}>
                <Text style={styles.peerName}>
                  Anonymous Peer {peer.anonymizedId.slice(-4)}
                </Text>
                <Text style={styles.peerStats}>
                  {peer.percentileRank}th percentile •{' '}
                  {peer.masteryAreas.length} skills
                </Text>
              </View>
              <View style={styles.peerScore}>
                <Text style={styles.scoreText}>
                  {Math.round(peer.skillLevel * 100)}
                </Text>
              </View>
            </View>
          ))}
        </View>
      )}
    </View>
  );

  const renderAchievementsTab = () => (
    <View style={styles.tabContent}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Collaborative Achievements</Text>
        <Text style={styles.sectionSubtitle}>
          Milestones earned through teamwork and peer learning
        </Text>
      </View>

      {/* Active Achievements */}
      <Text style={styles.subsectionTitle}>
        In Progress ({activeAchievements.length})
      </Text>
      {activeAchievements.map(achievement => (
        <CollaborativeAchievementCard
          key={achievement.id}
          achievement={achievement}
          onCelebrate={() => {}}
          onShare={() => {}}
        />
      ))}

      {/* Completed Achievements */}
      <Text style={styles.subsectionTitle}>
        Completed ({completedAchievements.length})
      </Text>
      {completedAchievements.map(achievement => (
        <CollaborativeAchievementCard
          key={achievement.id}
          achievement={achievement}
          onCelebrate={() => {}}
          onShare={() => {}}
        />
      ))}
    </View>
  );

  const renderOptimizationTab = () => (
    <View style={styles.tabContent}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Engagement Optimization</Text>
        <Text style={styles.sectionSubtitle}>
          AI-powered recommendations to enhance your social learning
        </Text>
        <TouchableOpacity
          style={styles.generateButton}
          onPress={handleGenerateOptimization}
          disabled={loading.optimization}
        >
          <Ionicons name='refresh' size={16} color='#fff' />
          <Text style={styles.generateButtonText}>Generate New Insights</Text>
        </TouchableOpacity>
      </View>

      {engagementOptimization && (
        <>
          <View style={styles.optimizationSummary}>
            <Text style={styles.cardTitle}>Current Engagement Score</Text>
            <View style={styles.scoreContainer}>
              <Text style={styles.currentScore}>
                {Math.round(
                  engagementOptimization.currentEngagementScore * 100
                )}
              </Text>
              <Text style={styles.potentialScore}>
                /{' '}
                {Math.round(
                  engagementOptimization.potentialEngagementScore * 100
                )}
              </Text>
            </View>
            <Text style={styles.scoreLabel}>Potential for improvement</Text>
          </View>

          {engagementOptimization.optimizationOpportunities.map(
            (opportunity, index) => (
              <EngagementOptimizationCard
                key={index}
                opportunity={opportunity}
                onImplement={() => {}}
              />
            )
          )}
        </>
      )}
    </View>
  );

  const renderTestsTab = () => (
    <View style={styles.tabContent}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>A/B Test Participation</Text>
        <Text style={styles.sectionSubtitle}>
          Help improve the platform by participating in feature experiments
        </Text>
      </View>

      {activeABTests.map(test => (
        <ABTestParticipationCard
          key={test.id}
          test={test}
          onOptOut={() => {}}
          onViewResults={() => {}}
        />
      ))}

      {activeABTests.length === 0 && (
        <View style={styles.emptyState}>
          <Ionicons name='flask-outline' size={48} color='#ccc' />
          <Text style={styles.emptyStateText}>No active experiments</Text>
          <Text style={styles.emptyStateSubtext}>
            Check back later for new feature tests to participate in
          </Text>
        </View>
      )}
    </View>
  );

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'overview':
        return renderOverviewTab();
      case 'benchmarks':
        return renderBenchmarksTab();
      case 'achievements':
        return renderAchievementsTab();
      case 'optimization':
        return renderOptimizationTab();
      case 'tests':
        return renderTestsTab();
      default:
        return renderOverviewTab();
    }
  };

  if (loading.comparisons && peerComparisons.length === 0) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <LoadingSpinner message='Loading social learning analytics...' />
      </SafeAreaView>
    );
  }

  if (error) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <ErrorMessage
          message={error}
          onRetry={() => user?.user_id && refreshAllData(user.user_id)}
        />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name='arrow-back' size={24} color='#333' />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Social Learning Analytics</Text>
        <TouchableOpacity style={styles.settingsButton}>
          <Ionicons name='settings-outline' size={24} color='#333' />
        </TouchableOpacity>
      </View>

      {/* Tab Navigation */}
      <View style={styles.tabNavigation}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {[
            { key: 'overview', label: 'Overview', icon: 'analytics' },
            { key: 'benchmarks', label: 'Benchmarks', icon: 'bar-chart' },
            { key: 'achievements', label: 'Achievements', icon: 'trophy' },
            { key: 'optimization', label: 'Optimization', icon: 'bulb' },
            { key: 'tests', label: 'A/B Tests', icon: 'flask' },
          ].map(tab => (
            <TouchableOpacity
              key={tab.key}
              style={[
                styles.tabButton,
                selectedTab === tab.key && styles.activeTabButton,
              ]}
              onPress={() => setSelectedTab(tab.key as any)}
            >
              <Ionicons
                name={tab.icon as any}
                size={16}
                color={selectedTab === tab.key ? '#2196F3' : '#666'}
              />
              <Text
                style={[
                  styles.tabButtonText,
                  selectedTab === tab.key && styles.activeTabButtonText,
                ]}
              >
                {tab.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Content */}
      <ScrollView
        style={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }
      >
        {renderTabContent()}
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  settingsButton: {
    padding: 8,
  },
  tabNavigation: {
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  tabButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginHorizontal: 4,
  },
  activeTabButton: {
    borderBottomWidth: 2,
    borderBottomColor: '#2196F3',
  },
  tabButtonText: {
    marginLeft: 6,
    fontSize: 14,
    color: '#666',
  },
  activeTabButtonText: {
    color: '#2196F3',
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  tabContent: {
    padding: 16,
  },
  sectionHeader: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#333',
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 12,
  },
  subsectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginTop: 20,
    marginBottom: 12,
  },
  generateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#2196F3',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    alignSelf: 'flex-start',
  },
  generateButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  summaryCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
  },
  cardSubtitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 12,
  },
  summaryGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  summaryItem: {
    alignItems: 'center',
    flex: 1,
  },
  summaryValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#2196F3',
  },
  summaryLabel: {
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
    marginTop: 4,
  },
  insightsCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  insightsList: {
    gap: 12,
  },
  insightItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  insightText: {
    fontSize: 14,
    color: '#333',
    marginLeft: 12,
    flex: 1,
  },
  topPeersCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  peerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  peerRank: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#2196F3',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  rankText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  peerInfo: {
    flex: 1,
  },
  peerName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  peerStats: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
  },
  peerScore: {
    alignItems: 'center',
  },
  scoreText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#4CAF50',
  },
  optimizationSummary: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  scoreContainer: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginVertical: 8,
  },
  currentScore: {
    fontSize: 36,
    fontWeight: '700',
    color: '#2196F3',
  },
  potentialScore: {
    fontSize: 24,
    fontWeight: '600',
    color: '#666',
  },
  scoreLabel: {
    fontSize: 14,
    color: '#666',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
  },
  emptyStateText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666',
    marginTop: 16,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
    marginTop: 8,
    paddingHorizontal: 32,
  },
});
